import {StructuredRemarksView} from './StructuredRemarksView'
import React, {FC, useMemo} from 'react'
import type {
  QHSESafetyCommitteeMeetingDto,
  SafetyCommitteeStatus,
  SafetyCommitteeClosureResult,
  SafetyCommitteeFindingLinkType,
} from '../core/_models'

type Props = {
  visible: boolean
  record: QHSESafetyCommitteeMeetingDto | null
  onCancel: () => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL_SMALL = 'fw-semibold text-muted d-block mb-1'

const getStatusDisplay = (
  record: {
    status?: SafetyCommitteeStatus | null
    officeReviewed?: boolean | null
    closureResult?: SafetyCommitteeClosureResult | null
  }
) => {
  const status = record?.status
  if (!status) {
    return {label: '-', badgeClass: 'badge-light'}
  }
  if (status === 'SAVE') {
    return {label: 'Saved', badgeClass: 'badge-light-secondary'}
  }
  if (status === 'SUBMIT') {
    if (record?.officeReviewed) {
      return {label: 'Reviewed', badgeClass: 'badge-light-success'}
    }
    return {label: 'Submitted', badgeClass: 'badge-light-warning'}
  }
  if (status === 'CLOSE') {
    if (record?.closureResult === 'TO_BE_REVIEWED_NEXT_INSPECTION') {
      return {label: 'Closed and TBR', badgeClass: 'badge-light-success'}
    }
    return {label: 'Closed Satisfactorily', badgeClass: 'badge-light-success'}
  }
  return {label: String(status), badgeClass: 'badge-light'}
}

export const ViewSCMModal: FC<Props> = ({
  visible,
  record,
  onCancel,
}) => {
  const statusMeta = useMemo(() => {
    if (!record) return {label: '-', badgeClass: 'badge-light'}
    return getStatusDisplay(record)
  }, [record])

  const meetingType = record?.meetingType || record?.findingsLinkType

  const meetingTypeLabel = useMemo(() => {
    if (!meetingType) return ''
    if (meetingType === 'AUDIT') return 'Audit'
    if (meetingType === 'INSPECTION') return 'Inspection'
    return 'Monthly'
  }, [meetingType])


  if (!visible || !record) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '75vw',
          height: '75vh',
          maxWidth: '1200px',
          maxHeight: '800px',
        }}
      >
        {/* Header */}
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <div className='d-flex flex-column'>
            <h5 className='modal-title text-dark m-6'>
              Safety Committee Meeting – {record.scmName || `SCM #${record.id}`}
            </h5>
            <div className='text-muted fs-7 mx-6'>
              Vessel:&nbsp;
              <span className='fw-semibold'>
                {record.vesselName || '-'}
              </span>
              {record.dateHeld && (
                <>
                  <span className='mx-2'>•</span>
                  Date Held:&nbsp;
                  <span className='fw-semibold'>
                    {record.dateHeld}
                  </span>
                </>
              )}
            </div>
          </div>

          <div className='d-flex align-items-center gap-3'>
            <span className={`badge ${statusMeta.badgeClass}`}>
              {statusMeta.label}
            </span>

            <button
              type='button'
              className='btn-close m-6'
              aria-label='Close'
              onClick={onCancel}
            />
          </div>
        </div>

        {/* Body with scroll */}
        <div className='flex-grow-1 overflow-auto px-4 py-3 m-6'>
          <div className='row g-4'>
            {/* Left column */}
            <div className='col-12'>
              <div className='mb-3'>
                <label className={LABEL_SMALL}>SCM Name</label>
                <div>{record.scmName || '-'}</div>
              </div>

              <div className='row'>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Date Held</label>
                  <div>{record.dateHeld || '-'}</div>
                </div>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Finding Number</label>
                  <div className='d-flex align-items-center gap-2'>
                    <span>{record.findingsNumber || '-'}</span>
                    {meetingTypeLabel && (
                      <span
                        className={`badge ${
                          meetingType === 'AUDIT'
                            ? 'badge-light-info'
                            : meetingType === 'INSPECTION'
                              ? 'badge-light-primary'
                              : 'badge-light-warning'
                        }`}
                      >
                        {meetingTypeLabel}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Present Safety Officer</label>
                  <div>{record.presentSafetyOfficer || '-'}</div>
                </div>
                <div className='col-md-6 mb-3'>
                  <label className={LABEL_SMALL}>Designated Safety Officer</label>
                  <div>{record.designatedSafetyOfficer || '-'}</div>
                </div>
              </div>

              <div className='mb-3'>
                <label className={LABEL_SMALL}>Master's / Ship's Remarks</label>
                <StructuredRemarksView text={record.mastersRemarks} emptyMessage='-' />
              </div>

              <div className='mb-3'>
                <label className={LABEL_SMALL}>Office Remarks</label>
                <StructuredRemarksView text={record.officeRemarks} emptyMessage='-' />
              </div>

              <div className='mb-3'>
                {/* Additional Info */}
                <div className='border-top pt-3 mt-3'>
                  <div className='row'>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Created By</label>
                      <div>
                        <span
                          className={`badge ${
                            record.createdByRole === 'CREW'
                              ? 'badge-light-info'
                              : 'badge-light-primary'
                          }`}
                        >
                          {record.createdByRole || '-'}
                        </span>
                      </div>
                    </div>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Submitted At</label>
                      <div>{record.submittedAt || '-'}</div>
                    </div>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Office Reviewed</label>
                      <div>{record.officeReviewed ? 'Yes' : 'No'}</div>
                    </div>
                    <div className='col-md-6 mb-2'>
                      <label className={LABEL_SMALL}>Closure Result</label>
                      <div>
                        {record.closureResult === 'SATISFACTORY'
                          ? 'Satisfactory'
                          : record.closureResult === 'TO_BE_REVIEWED_NEXT_INSPECTION'
                          ? 'To Be Reviewed Next Inspection'
                          : '-'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onCancel}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}
